/**
 * AIClassifyPage · 页面总装（v4）
 *
 * 布局结构（左右两栏 + 拖拽分割线）：
 *   Body (flex row)
 *     ├── 左栏 (column · header「照片分类」+ FolderList)
 *     ├── ResizeHandle（仅 sidebarOpen 时渲染）
 *     └── AISidebar (含 header「AI 分类助手」+ AIChat)
 *
 * 顶部按钮组（右上角，从左到右）：
 *   · 视图切换（grid / list；ghost 选中只描边不实心）
 *   · 多选（管理员可见，进入多选模式；底部操作条同时给「下载」+「删除」）
 *   · 分类任务（仅有任务时作为独立按钮，无 icon；无任务收进 ⋯）
 *   · ⋯ 更多（恒在，含「下载记录」+ 无任务时的「分类任务」入口）
 *   · AI 助手（仅 sidebar 关闭时显示）
 *
 * v4 改动：会话驱动从 useMockAISession 切到 useAIClassifySession。
 * sendUserMessage API 通过 ChatActionsProvider 下发给 Composer。
 */

import React, { useState } from 'react'
import { Layout } from 'antd'
import PageHeader from '@/components/PageHeader'
import classNames from 'classnames'
import { useAppDispatch, useAppSelector, useIsAdmin } from '../../app/hooks'
import {
  selectHasFolders,
  selectFolderListLoaded,
  selectSidebarOpen,
  selectSidebarWidth,
  selectFolderRefreshTick,
  applySidebarDefault,
  closeSidebar,
  setSidebarWidth
} from '../../slices/aiClassify'
import { fetchFolderQuota } from '../../slices/folderQuotaSlice'
import { useFolderCreateGate } from '../components/FolderPaywall/useFolderCreateGate'
import DownloadHistory from '../components/DownloadHistory'
import FolderList from './components/FolderList'
import { FolderBatchActionsProvider } from './components/FolderList/FolderBatchActions'
import FolderHeaderActions from './components/FolderHeaderActions'
import AISidebar from './components/AISidebar'
import ResizeHandle from './components/AISidebar/ResizeHandle'
import { useResizable } from './components/AISidebar/hooks/useResizable'
import AIChat from './components/AIChat'
import AIChatHeaderActions from './components/AIChatHeaderActions'
import ArchiveTaskCompletionToasts from './components/ArchiveTaskCompletionToasts'
import CreateFolderModal from './components/CreateFolderModal'
import RecycleBinDrawer from './components/RecycleBinDrawer'
import { ChatActionsProvider } from './components/AIChat/ChatActionsContext'
import { useAIClassifySession } from './hooks/useAIClassifySession'
import { useAIClassifyLifecycle } from './hooks/useAIClassifyLifecycle'
import { useInProgressReconnect } from './hooks/useInProgressReconnect'
import { selectConversationId } from '../../slices/aiClassify'
import { trackClassifyClick, trackAIChatClick } from './utils/trackClassify'
import styles from './AIClassifyPage.module.scss'

const { Content } = Layout

/** Sidebar 宽度范围 · 与视觉稿对齐 */
const SIDEBAR_MIN_WIDTH = 360
const SIDEBAR_MAX_WIDTH = 720

const AIClassifyPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const sidebarOpen = useAppSelector(selectSidebarOpen)
  const sidebarWidth = useAppSelector(selectSidebarWidth)
  // 是否有文件夹 · 决定顶部操作工具栏显隐（空态隐藏，对应图1）
  const hasFolders = useAppSelector(selectHasFolders)
  /**
   * 多选 / 删除 / 下载 都走 isAdmin · 与 photoFolders/Detail/HeaderExtra:370
   * 现有规则一致；普通成员调批量删除会被 BFF 以 -203 拒绝，UI 直接隐藏入口。
   */
  const isAdmin = useIsAdmin()
  // 回收站抽屉显隐 · 仅 admin 入口；非 admin 强行打开也会被 BFF -203 拒绝
  const [recycleBinOpen, setRecycleBinOpen] = useState(false)

  /** 「新建文件夹」弹窗开关 · 仅管理员可见入口 */
  const [createModalOpen, setCreateModalOpen] = useState(false)

  // v4 SSE 会话驱动 · 在树顶层挂一份；通过 Context 下发 sendUserMessage
  const chatActions = useAIClassifySession()
  // 任务列表 hydrate + 10s 轮询（红点闪烁 / 任务进度等）
  // 历史上曾与 photoFolders/AIChatDrawer 共享，AIChatDrawer 已下掉，本页面是
  // AI 助手的唯一入口
  useAIClassifyLifecycle()
  // 豆包式"刷新接上"：用户和 agent 对话时刷新 → SSE 断 → 后端 agent 后台仍在跑
  // → 入口写的 InProgress 行被 progressPersister 节流 UPDATE assistant_response
  // → 本 hook 检测 history 里 hist-asst-* 的 streaming 消息，1.5s 轮询拉最新内容
  // 直到 status 变 done/error 自动停。
  const conversationId = useAppSelector(selectConversationId)
  const groupID = useAppSelector((s: any) => s.group.groupId)
  useInProgressReconnect({
    conversationId: conversationId || '',
    groupID: groupID || ''
  })

  // ── 单个创建付费卡点（PRD §3.2.3）──────────────────────────
  const singleCreateGate = useFolderCreateGate()
  const refreshTick = useAppSelector(selectFolderRefreshTick)

  // 卡点需要 used/cap：进页面 + 增删后拉一次接口4（含非会员，配额 widget 对非会员不拉）
  React.useEffect(() => {
    if (groupID) dispatch(fetchFolderQuota({ groupID }))
  }, [groupID, refreshTick, dispatch])

  // 侧边栏默认开合：列表首次加载成功后，有文件夹默认展开、空列表保持收起；
  // 只生效一次（reducer 内有标记防重复），用户手动开合优先
  const folderListLoaded = useAppSelector(selectFolderListLoaded)
  React.useEffect(() => {
    if (folderListLoaded) dispatch(applySidebarDefault(hasFolders))
  }, [folderListLoaded, hasFolders, dispatch])

  /**
   * 「新建文件夹」前的卡点 · 已达上限 → gate 内拉开通弹窗（不开建夹弹窗）；否则照常。
   * 列表页 header / 缺省页 / 右键空白菜单都走 onCreateFolder → 本 handler，统一拦截。
   */
  const handleCreateFolder = () => {
    if (!singleCreateGate()) return
    setCreateModalOpen(true)
  }

  React.useEffect(() => {
    trackClassifyClick('enterPage')
  }, [])

  const { width, handleMouseDown, isDragging } = useResizable({
    initialWidth: sidebarWidth,
    minWidth: SIDEBAR_MIN_WIDTH,
    maxWidth: SIDEBAR_MAX_WIDTH,
    onEnd: (w) => dispatch(setSidebarWidth(w))
  })

  return (
    <ChatActionsProvider value={chatActions}>
      <Layout className={styles.page}>
        <Content className={styles.body}>
          <div className={styles.folderColumn}>
            {/* 页头 · PageHeader（默认样式，结构对齐其它 PageHeader 页面）
                title/subTitle 原生（v3 副标题行内显示）；extra = 右侧操作区，
                仅有文件夹时显示：新建文件夹 / AI 批量分类 / ⋯ 更多（恒显常规按钮，
                多选态批量按钮已下移到列表操作栏 FolderListToolbar） */}
            <PageHeader
              ghost={false}
              style={{ borderBottom: '1px solid #ebedf0' }}
              title="分类文件夹"
              subTitle="照片自动分类，多端共享，团队协作"
              extra={
                hasFolders ? (
                  <FolderHeaderActions
                    onCreateFolder={handleCreateFolder}
                    onOpenRecycleBin={() => setRecycleBinOpen(true)}
                  />
                ) : undefined
              }
            />
            <div className={styles.folderArea}>
              <FolderBatchActionsProvider groupID={groupID || ''}>
                <FolderList
                  onCreateFolder={isAdmin ? handleCreateFolder : undefined}
                />
              </FolderBatchActionsProvider>
            </div>
          </div>
          {sidebarOpen && (
            <ResizeHandle
              onMouseDown={handleMouseDown}
              isDragging={isDragging}
            />
          )}
          <AISidebar
            visible={sidebarOpen}
            onClose={() => {
              trackAIChatClick('closeAI')
              dispatch(closeSidebar())
            }}
            width={width}
            className={classNames({ [styles.sidebarDragging]: isDragging })}
            headerActions={<AIChatHeaderActions showTasks={false} />}
          >
            <AIChat />
          </AISidebar>
        </Content>
        {/* 下载记录抽屉 · 由 ⋯ 菜单的「下载记录」触发；老页面 /photo/folders 同款，
            visible / 关闭 / 任务列表全部走 redux state.photo + photo actions */}
        <DownloadHistory />
        {/* 新建文件夹弹窗 · 仅管理员触发；成功后 bumpFolderRefreshTick + 跳新详情 */}
        <CreateFolderModal
          visible={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
        />
        {/* 回收站抽屉 · 由「回收站」按钮触发；列已软删除文件夹 + 单项恢复 */}
        <RecycleBinDrawer
          visible={recycleBinOpen}
          onClose={() => setRecycleBinOpen(false)}
        />
        {/*
         * 任务完成通知 (老归档任务路径 · archive task localStorage 体系)
         * 注意: AI 建夹任务的"文件夹创建任务"抽屉已整体下线 (改为单任务静默轮询 +
         * 会话内消息通知), 这里的归档任务完成通知点击暂时不打开抽屉 —— 老归档任务
         * 后续如有展示需求需要新独立入口承接。
         */}
        <ArchiveTaskCompletionToasts onOpenDrawer={() => undefined} />
      </Layout>
    </ChatActionsProvider>
  )
}

export default AIClassifyPage
